import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError, isRecord, request, requireArray, requireBoolean, requireString, withQuery } from '@/lib/api'
import { Avatar } from '@/ui/avatar'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { formatDateTime } from '@/lib/format-date'
import { DataGrid, type auditLogGridContract, type DataGridColumnRenderers } from '@/modules/data-grid'

import { auditLogRenderers } from '../../audit-log-renderers'
import { SUBJECT_COLUMNS } from '../../routes'

import { useSubjectAuditLogGrid, type AuditSubjectType } from './subject-audit-log-queries'

const subjectRenderers: DataGridColumnRenderers<typeof auditLogGridContract> = {
  ...auditLogRenderers,
  timestamp: {
    cell: ({ value }) => value ? <time dateTime={value}>{formatDateTime(value)}</time> : '—',
  },
}

export function SubjectAuditLog({
  entityId,
  subjectType,
  subjectId,
  urlState,
  caption,
}: {
  entityId: string
  subjectType: AuditSubjectType
  subjectId: string
  urlState: boolean
  caption: string
}) {
  const grid = useSubjectAuditLogGrid(entityId, subjectType, subjectId, urlState)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const policyHistory = subjectType === 'Policy'
  const newest = grid.items[0]?.key
  if (grid.query.error instanceof ApiError && grid.query.error.status === 403)
    return <p role="alert" className="text-destructive text-sm">You don’t have permission to view this audit log.</p>
  return (
    <DataGrid
      grid={grid}
      caption={caption}
      regionLabel="Audit activity"
      noun="records"
      searchLabel="audit log"
      emptyMessage={grid.activeCriteria.search || grid.activeCriteria.filter?.length
        ? 'No activity matches your search or filters.'
        : 'No activity recorded yet.'}
      columnChooserColumns={SUBJECT_COLUMNS}
      renderers={policyHistory ? policyRenderers : subjectRenderers}
      rowDetail={policyHistory ? {
        hasDetail: () => true,
        isExpanded: (row) => expanded[row.key] ?? row.key === newest,
        onToggle: (row) => setExpanded((current) => ({
          ...current,
          [row.key]: !(current[row.key] ?? row.key === newest),
        })),
        label: (row) => `Details: ${row.values.details ?? row.values.action}, ${row.values.timestamp ?? ''}`,
        render: (row) => <PolicyChanges entityId={entityId} policyId={subjectId} activityId={row.key} />,
      } : undefined}
    />
  )
}

const policyRenderers: typeof subjectRenderers = {
  ...subjectRenderers,
  actor: {
    cell: (context) => (
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={context.value ?? ''} />
        {auditLogRenderers.actor?.cell?.(context)}
      </div>
    ),
  },
}

function decodePolicyChanges(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed policy change response')
  return requireArray(raw.changes, 'changes', (change) => {
    if (!isRecord(change)) throw new Error('Malformed policy change')
    return {
      id: requireString(change.id, 'id'),
      fields: requireArray(change.fields, 'fields', (field) => {
        if (!isRecord(field)) throw new Error('Malformed policy change field')
        return {
          path: requireString(field.path, 'path'),
          masked: requireBoolean(field.masked, 'masked'),
          old: field.old,
          value: field.value,
        }
      }),
    }
  })
}

function displayValue(value: unknown, masked: boolean) {
  if (masked) return 'Hidden'
  if (value === undefined) return '—'
  return typeof value === 'string' ? value : JSON.stringify(value)
}

function PolicyChanges({ entityId, policyId, activityId }: {
  entityId: string
  policyId: string
  activityId: string
}) {
  const query = useQuery({
    queryKey: ['audit', 'policy-changes', entityId, policyId, activityId],
    queryFn: ({ signal }) => request(
      withQuery(`/api/v1/entities/${encodeURIComponent(entityId)}/history/${encodeURIComponent(activityId)}/changes`, { policyId }),
      decodePolicyChanges,
      { signal },
    ),
  })

  if (query.isPending) return <LoadingNotice message="Loading changes…" />
  if (query.isError) return <RetryNotice message="Couldn’t load this change." onRetry={() => void query.refetch()} />
  const changes = query.data
  const hasPrevious = changes.some((change) => change.fields.some((field) => field.old !== undefined))
  if (changes.every((change) => change.fields.length === 0)) {
    return <p className="text-muted-foreground">No field changes recorded.</p>
  }

  return (
    <div className="border-border overflow-hidden rounded-md border">
      <table className="w-full text-xs">
        <caption className="sr-only">Changed properties</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left">
          <tr>
            <th scope="col" className="px-3 py-1.5">Property</th>
            {hasPrevious && <th scope="col" className="px-3 py-1.5">Previous</th>}
            <th scope="col" className="px-3 py-1.5">{hasPrevious ? 'New' : 'Value'}</th>
          </tr>
        </thead>
        <tbody>
          {changes.flatMap((change) => change.fields.map((field) => (
            <tr key={change.id + field.path} className="border-border border-t">
              <td className="text-muted-foreground w-1/3 px-3 py-1.5">{field.path}</td>
              {hasPrevious && (
                <td className="text-destructive w-1/3 break-all px-3 py-1.5 line-through">
                  {displayValue(field.old, field.masked)}
                </td>
              )}
              <td className="w-1/3 break-all px-3 py-1.5">{displayValue(field.value, field.masked)}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  )
}
